// 选号下单页：选红蓝球 → 选倍数 → 提交
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, Button, Stack,
  Alert, IconButton, TextField, Chip, Divider, CircularProgress,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ClearIcon from '@mui/icons-material/Clear';
import RlPageHeader from '../../components/real-lottery/RlPageHeader';
import BallPicker, { BallDisplay } from '../../components/real-lottery/BallPicker';
import { getCurrentRlUser } from '../../components/real-lottery/useRlUser';
import {
  getCurrentPeriod, getRlPoints, createRlOrder, getRlStatus,
} from '../../services/realLotteryApi';

export default function RealLotteryPickPage() {
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [period, setPeriod] = useState(null);
  const [points, setPoints] = useState(0);
  const [pointsPerBet, setPointsPerBet] = useState(10);
  const [redBalls, setRedBalls] = useState([]);
  const [blueBall, setBlueBall] = useState('');
  const [multiple, setMultiple] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const u = getCurrentRlUser();
    if (!u) { history.replace('/login-v2'); return; }
    setUser(u);
    Promise.all([
      getCurrentPeriod('ssq'),
      getRlPoints(u.userId),
      getRlStatus(),
    ]).then(([p, pt, st]) => {
      if (p.success) setPeriod(p.data);
      if (pt.success) setPoints(pt.data.points);
      if (st.success) setPointsPerBet(st.data.pointsPerBet);
      setLoading(false);
    });
  }, []);

  const cost = pointsPerBet * 1 * multiple; // 1 注 × 倍数（自选单注）
  const isReady = redBalls.length === 6 && blueBall && multiple >= 1 && points >= cost;

  const handleRandomRed = () => {
    const arr = [];
    while (arr.length < 6) {
      const n = String(Math.floor(Math.random() * 33) + 1).padStart(2, '0');
      if (!arr.includes(n)) arr.push(n);
    }
    setRedBalls(arr.sort());
  };
  const handleRandomBlue = () => {
    setBlueBall(String(Math.floor(Math.random() * 16) + 1).padStart(2, '0'));
  };
  const handleRandomAll = () => {
    handleRandomRed();
    handleRandomBlue();
  };
  const handleClear = () => {
    setRedBalls([]); setBlueBall('');
  };

  const handleSubmit = async () => {
    if (!isReady) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await createRlOrder({
        userId: user.userId,
        gameCode: 'ssq',
        period: period.period,
        redBalls,
        blueBall,
        betCount: 1,
        multiple,
      });
      if (res.success) {
        history.replace(`/real-lottery/order/${res.data.order.outTradeNo}?fresh=1`);
      } else {
        setError(res.message || '下单失败');
        if (res.code === 'KYC_REQUIRED') {
          setTimeout(() => history.push('/real-lottery'), 1500);
        }
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f7', minHeight: '100vh', pb: 12 }}>
      <RlPageHeader
        title="选号下单"
        subtitle={period ? `第 ${period.period} 期 · 截止 ${period.endSaleTime || ''}` : ''}
      />

      <Container maxWidth="sm" sx={{ pt: 2 }}>
        {/* 已选号码预览 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">已选号码</Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRandomAll} title="机选"><ShuffleIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={handleClear} title="清空"><ClearIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
            {redBalls.length === 0 && !blueBall ? (
              <Typography variant="caption" color="text.secondary">请选择 6 个红球（01-33）+ 1 个蓝球（01-16）</Typography>
            ) : (
              <BallDisplay red={redBalls} blue={blueBall} size={32} />
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`红 ${redBalls.length}/6`}
                color={redBalls.length === 6 ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label={`蓝 ${blueBall ? '1/1' : '0/1'}`}
                color={blueBall ? 'success' : 'default'}
                size="small"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* 红球选择 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: '#e53935' }}>红球（选 6 个）</Typography>
              <Button size="small" onClick={handleRandomRed} startIcon={<ShuffleIcon />}>机选</Button>
            </Stack>
            <BallPicker type="red" selected={redBalls} onChange={setRedBalls} maxSelect={6} />
          </CardContent>
        </Card>

        {/* 蓝球选择 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: '#1976d2' }}>蓝球（选 1 个）</Typography>
              <Button size="small" onClick={handleRandomBlue} startIcon={<ShuffleIcon />}>机选</Button>
            </Stack>
            <BallPicker
              type="blue"
              selected={blueBall ? [blueBall] : []}
              onChange={(arr) => setBlueBall(arr[arr.length - 1] || '')}
              maxSelect={1}
            />
          </CardContent>
        </Card>

        {/* 倍数 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">投注倍数</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={() => setMultiple(Math.max(1, multiple - 1))}>
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={multiple}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v)) setMultiple(Math.min(99, Math.max(1, v)));
                  }}
                  size="small"
                  sx={{ width: 60 }}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <IconButton size="small" onClick={() => setMultiple(Math.min(99, multiple + 1))}>
                  <AddIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              单注 {pointsPerBet} 积分 × {multiple} 倍 = {cost} 积分
            </Typography>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </Container>

      {/* 底部固定提交栏 */}
      <Box sx={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        backgroundColor: '#fff', borderTop: '1px solid rgba(0,0,0,0.08)',
        py: 1.5, px: 2, zIndex: 10,
      }}>
        <Container maxWidth="sm" disableGutters>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary">需消耗</Typography>
              <Typography variant="h6" color={points >= cost ? 'error' : 'text.disabled'}>
                {cost} 积分
              </Typography>
              <Typography variant="caption" color="text.secondary">
                余额 {points} 分
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              disabled={!isReady || submitting}
              onClick={handleSubmit}
              sx={{ minWidth: 140, py: 1.3 }}
            >
              {submitting ? '提交中…' : '确认下单'}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
